
import { defineComponent, PropType } from "vue";
import DraftTreeView from "./DraftTreeView.vue";
import { buildMmiForest, DraftForest } from "./draftTree";
import { buildMmiExportHtml, draftExportBaseName } from "./draftTreeExport";
import { MigrationMmiDraft, MigrationMmiDraftNode } from "./types";

type ViewKey = "tree" | "table";

/**
 * LA REVISIÓN DEL DRAFT DEL CMMI, con el árbol dibujado y la tabla de siempre.
 *
 * EL ÁRBOL EXISTE POR EL PASE. Un `isPassUp` significa que esa persona no
 * cuelga de quien la patrocinó: su patrocinador no calificaba para recibirla y
 * subió a alguien de más arriba de la cadena. En la tabla eso es una casilla que
 * dice «sí» y no explica nada — hay que ir cruzando padres a mano para entender
 * quién acabó debajo de quién. Dibujado, el salto se ve.
 *
 * LA TABLA SE QUEDA, y no es un adorno: los `isPassUp` se editan por SWAP entre
 * hermanos (§21.2, D11), y para eso una lista con las dos columnas —viejo y
 * nuevo— al lado se lee mejor que un dibujo.
 *
 * REUSA `DraftTreeView` y `DraftTreeSubtree`, los mismos del árbol binario y el
 * de patrocinio. Lo único propio del CMMI es cómo se arma su tarjeta, y eso vive
 * en `draftTree.ts` (`buildMmiForest`), no aquí.
 */
export default defineComponent({
  name: "MmiDraftPreview",
  components: { DraftTreeView },
  props: {
    draft: { type: Object as PropType<MigrationMmiDraft>, required: true },
  },
  data() {
    return {
      // Se abre por el ÁRBOL: es lo que no se entiende en la tabla.
      view: "tree" as ViewKey,
      tabs: [
        {
          key: "tree" as ViewKey,
          label: "Árbol CMMI",
          icon: "fa-diagram-project",
        },
        { key: "table" as ViewKey, label: "Tabla", icon: "fa-table" },
      ],
      onlyChanged: false,
      exportError: "",
    };
  },
  computed: {
    forest(): DraftForest {
      return buildMmiForest(this.draft);
    },
    rows(): MigrationMmiDraftNode[] {
      const nodes = this.draft.nodes ?? [];
      return this.onlyChanged ? nodes.filter((n) => this.changed(n)) : nodes;
    },
    changedCount(): number {
      return (this.draft.nodes ?? []).filter((n) => this.changed(n)).length;
    },
  },
  methods: {
    changed(n: MigrationMmiDraftNode): boolean {
      return (
        (n.legacyParent != null && n.legacyParent !== n.parentPK) ||
        (n.legacyIsPassUp != null && n.legacyIsPassUp !== n.isPassUp)
      );
    },
    date(ms: number): string {
      return new Date(ms).toISOString().slice(0, 10);
    },
    /**
     * EL FICHERO DEL CMMI, APARTE DEL DE LOS OTROS DOS ÁRBOLES.
     *
     * No es una elección estética: el paso del CMMI DEPENDE del de los árboles
     * (`requires: ["build-trees--c"]`), porque el CMMI se calcula caminando la
     * cadena de patrocinio. Nunca hay los dos borradores en revisión a la vez,
     * y un fichero que los juntara estaría cosiendo dos momentos distintos.
     */
    exportHtml(): void {
      this.exportError = "";
      try {
        const now = Date.now();
        const html = buildMmiExportHtml(this.draft, {
          runLabel: this.draft.generatedFor || "",
          generatedAt: now,
          migrationDate: this.draft.migrationDate,
          root: this.draft.root,
        });
        const blob = new Blob([html], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${draftExportBaseName(
          this.draft.generatedFor,
          now,
          "arbol-cmmi"
        )}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      } catch (e) {
        this.exportError = (e as Error)?.message || String(e);
      }
    },
  },
});
